import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { API_URL, getLicenseBadgeClass } from '../lib/utils';
import { toast } from 'sonner';
import LicenseVerifiedBadge from '../components/LicenseVerifiedBadge';
import {
  Search,
  MapPin,
  Briefcase,
  User,
  Mail,
  Phone,
  FileText,
  Loader2,
  Lock,
  Crown,
  Star,
  Clock,
  ArrowLeft,
} from 'lucide-react';

const CandidateBrowse = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isEmployer, isAdmin } = useAuth();
  
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  
  const [filters, setFilters] = useState({
    search: searchParams.get('search') || '',
    license_type: searchParams.get('license') || 'all',
    location: searchParams.get('location') || '',
  });

  useEffect(() => {
    if (!isEmployer && !isAdmin) {
      toast.error('Only employers can browse candidates');
      navigate('/');
      return;
    }
    fetchCandidates();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, isEmployer, isAdmin]);

  const fetchCandidates = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      
      if (searchParams.get('search')) params.append('search', searchParams.get('search'));
      if (searchParams.get('license') && searchParams.get('license') !== 'all') {
        params.append('license_type', searchParams.get('license'));
      }
      if (searchParams.get('location')) params.append('location', searchParams.get('location'));
      params.append('page', searchParams.get('page') || '1');
      
      const response = await axios.get(`${API_URL}/candidates?${params.toString()}`, {
        headers: {  },
      });
      
      setCandidates(response.data.candidates);
      setPagination({
        page: response.data.page,
        pages: response.data.pages,
        total: response.data.total,
      });
      setHasAccess(true);
    } catch (error) {
      if (error.response?.status === 403) {
        setHasAccess(false);
      } else {
        toast.error('Failed to load candidates');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (filters.search) params.set('search', filters.search);
    if (filters.license_type && filters.license_type !== 'all') params.set('license', filters.license_type);
    if (filters.location) params.set('location', filters.location);
    setSearchParams(params);
  };

  const viewCandidateDetail = async (userId) => {
    setDetailLoading(true);
    try {
      const response = await axios.get(`${API_URL}/candidates/${userId}`, {
        headers: {  },
      });
      setSelectedCandidate(response.data);
    } catch (error) {
      toast.error('Failed to load candidate details');
    } finally {
      setDetailLoading(false);
    }
  };

  // Show subscription required message
  if (!loading && !hasAccess) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20">
        <div className="max-w-2xl mx-auto px-4 py-16 text-center">
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
            <div className="w-20 h-20 rounded-full bg-secondary/10 flex items-center justify-center mx-auto mb-6">
              <Lock className="w-10 h-10 text-secondary" />
            </div>
            <h1 className="font-serif text-2xl font-bold text-gray-900 mb-4">
              Subscription Required
            </h1>
            <p className="text-gray-600 mb-6">
              Browse qualified LMSW and LCSW candidates with an active subscription.
              Get access to resumes, contact information, and more.
            </p>
            <Link to="/employer/subscription">
              <Button className="bg-secondary hover:bg-secondary-hover text-white px-8">
                <Crown className="w-4 h-4 mr-2" />
                View Subscription Plans
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <button onClick={() => navigate('/employer/dashboard')} className="text-gray-500 hover:text-primary">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="font-serif text-3xl font-bold text-gray-900">
              Browse Candidates
            </h1>
            <p className="text-gray-600">
              {pagination.total} qualified social workers available
            </p>
          </div>
        </div>

        {/* Search */}
        <form onSubmit={handleSearch} className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 mb-6">
          <div className="grid md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Name, skills..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="pl-10"
              />
            </div>
            <Select
              value={filters.license_type}
              onValueChange={(value) => setFilters({ ...filters, license_type: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="License Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Licenses</SelectItem>
                <SelectItem value="LCSW">LCSW</SelectItem>
                <SelectItem value="LMSW">LMSW</SelectItem>
                <SelectItem value="LBSW">LBSW</SelectItem>
                <SelectItem value="SWA">Social Work Associate</SelectItem>
              </SelectContent>
            </Select>
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Location"
                value={filters.location}
                onChange={(e) => setFilters({ ...filters, location: e.target.value })}
                className="pl-10"
              />
            </div>
            <Button type="submit" className="bg-primary hover:bg-primary-hover text-white">
              Search
            </Button>
          </div>
        </form>

        {/* Results */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : candidates.length > 0 ? (
          <>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {candidates.map((candidate) => (
                <div
                  key={candidate.user_id}
                  className="bg-white rounded-xl p-6 border border-gray-100 hover:border-secondary/50 hover:shadow-md transition-all cursor-pointer"
                  onClick={() => viewCandidateDetail(candidate.user_id)}
                >
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      {candidate.picture ? (
                        <img src={candidate.picture} alt="" className="w-14 h-14 rounded-full" />
                      ) : (
                        <User className="w-6 h-6 text-primary" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 truncate">{candidate.name}</h3>
                      {candidate.license_type && (
                        <Badge className={`${getLicenseBadgeClass(candidate.license_type)} text-xs mt-1`}>
                          {candidate.license_type}
                        </Badge>
                      )}
                    </div>
                  </div>
                  
                  {candidate.location && (
                    <div className="flex items-center gap-2 mt-4 text-sm text-gray-500">
                      <MapPin className="w-4 h-4" />
                      {candidate.location}
                    </div>
                  )}
                  
                  {candidate.years_experience && (
                    <div className="flex items-center gap-2 mt-2 text-sm text-gray-500">
                      <Briefcase className="w-4 h-4" />
                      {candidate.years_experience} years experience
                    </div>
                  )}
                  
                  {candidate.specializations && candidate.specializations.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-3">
                      {candidate.specializations.slice(0, 3).map((spec, i) => (
                        <Badge key={i} variant="outline" className="text-xs">
                          {spec}
                        </Badge>
                      ))}
                    </div>
                  )}
                  
                  <Button variant="outline" size="sm" className="w-full mt-4">
                    View Profile
                  </Button>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="flex justify-center gap-2 mt-8">
                <Button
                  variant="outline"
                  disabled={pagination.page <= 1}
                  onClick={() => {
                    const params = new URLSearchParams(searchParams);
                    params.set('page', String(pagination.page - 1));
                    setSearchParams(params);
                  }}
                >
                  Previous
                </Button>
                <span className="flex items-center px-4 text-sm text-gray-600">
                  Page {pagination.page} of {pagination.pages}
                </span>
                <Button
                  variant="outline"
                  disabled={pagination.page >= pagination.pages}
                  onClick={() => {
                    const params = new URLSearchParams(searchParams);
                    params.set('page', String(pagination.page + 1));
                    setSearchParams(params);
                  }}
                >
                  Next
                </Button>
              </div>
            )}
          </>
        ) : (
          <div className="bg-white rounded-xl border border-gray-100 text-center py-16">
            <User className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="font-semibold text-gray-900 mb-2">No candidates found</h3>
            <p className="text-gray-500">Try adjusting your search filters</p>
          </div>
        )}
      </div>

      {/* Candidate Detail Dialog */}
      <Dialog open={!!selectedCandidate} onOpenChange={() => setSelectedCandidate(null)}>
        <DialogContent className="sm:max-w-lg">
          {detailLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : selectedCandidate && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                    {selectedCandidate.picture ? (
                      <img src={selectedCandidate.picture} alt="" className="w-16 h-16 rounded-full" />
                    ) : (
                      <User className="w-8 h-8 text-primary" />
                    )}
                  </div>
                  <div>
                    <DialogTitle className="font-serif text-xl">{selectedCandidate.name}</DialogTitle>
                    {selectedCandidate.license_type && (
                      <Badge className={`${getLicenseBadgeClass(selectedCandidate.license_type)} mt-1`}>
                        {selectedCandidate.license_type}
                      </Badge>
                    )}
                  </div>
                </div>
              </DialogHeader>

              <div className="space-y-4 py-4">
                <LicenseVerifiedBadge userId={selectedCandidate.user_id} />
                {selectedCandidate.email && (
                  <div className="flex items-center gap-3 text-sm">
                    <Mail className="w-4 h-4 text-gray-400" />
                    <a href={`mailto:${selectedCandidate.email}`} className="text-primary hover:underline">
                      {selectedCandidate.email}
                    </a>
                  </div>
                )}
                
                {selectedCandidate.phone && (
                  <div className="flex items-center gap-3 text-sm">
                    <Phone className="w-4 h-4 text-gray-400" />
                    <a href={`tel:${selectedCandidate.phone}`} className="text-gray-700">
                      {selectedCandidate.phone}
                    </a>
                  </div>
                )}
                
                {selectedCandidate.location && (
                  <div className="flex items-center gap-3 text-sm">
                    <MapPin className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-700">{selectedCandidate.location}</span>
                  </div>
                )}
                
                {selectedCandidate.years_experience && (
                  <div className="flex items-center gap-3 text-sm">
                    <Clock className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-700">{selectedCandidate.years_experience} years experience</span>
                  </div>
                )}
                
                {selectedCandidate.bio && (
                  <div className="pt-4 border-t">
                    <h4 className="font-medium text-gray-900 mb-2">About</h4>
                    <p className="text-sm text-gray-600">{selectedCandidate.bio}</p>
                  </div>
                )}
                
                {selectedCandidate.specializations && selectedCandidate.specializations.length > 0 && (
                  <div className="pt-4 border-t">
                    <h4 className="font-medium text-gray-900 mb-2">Specializations</h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedCandidate.specializations.map((spec, i) => (
                        <Badge key={i} variant="outline">{spec}</Badge>
                      ))}
                    </div>
                  </div>
                )}
                
                {selectedCandidate.resume_url && (
                  <div className="pt-4 border-t">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <FileText className="w-4 h-4" />
                      <span>Resume: {selectedCandidate.resume_url}</span>
                    </div>
                  </div>
                )}
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setSelectedCandidate(null)}>
                  Close
                </Button>
                {selectedCandidate.email && (
                  <Button
                    className="bg-primary hover:bg-primary-hover text-white"
                    onClick={() => window.location.href = `mailto:${selectedCandidate.email}`}
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    Contact
                  </Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CandidateBrowse;
